/*
 * Um menu de sugestões preso ao cursor do editor, como os de
 * `:emoji` e `#categoria`.
 *
 * Segue o desenho do autocompletar do flarum/mentions (um contêiner logo
 * depois do textarea, posição calculada pelas coordenadas do cursor e as
 * setas/Enter/Tab/Esc pelo KeyboardNavigatable), mas como uma classe só,
 * reaproveitada pelos dois gatilhos do tema.
 */
import KeyboardNavigatable from 'flarum/common/utils/KeyboardNavigatable';
import AutocompleteReader from 'flarum/common/utils/AutocompleteReader';
import classList from 'flarum/common/utils/classList';
import { editorDriver } from './common';
export default class Autocomplete {
  active = false;
  index = 0;
  items = [];
  start = 0;
  container = null;
  textarea = null;
  request = 0;
  constructor(editor, options) {
    this.editor = editor;
    this.options = options;
  }

  /** Chamado no `onbuild` do editor: o textarea já está no DOM. */
  attach() {
    const textarea = this.editor.element?.querySelector('.TextEditor-editor');
    if (!textarea || !textarea.parentElement) return;
    this.textarea = textarea;
    this.container = document.createElement('div');
    this.container.className = 'CanelaAutocomplete-container';
    textarea.parentElement.style.position ||= 'relative';
    textarea.after(this.container);
    new KeyboardNavigatable().when(() => this.active && this.items.length > 0).onUp(() => this.move(-1)).onDown(() => this.move(1)).onSelect(() => this.choose(this.index)).onCancel(() => this.hide()).bindTo($(textarea));
  }

  /** O ouvinte de digitação, posto em `params.inputListeners`. */
  listener = () => {
    const driver = editorDriver(this.editor);
    if (!driver || !this.container) return;
    const [cursor, end] = driver.getSelectionRange();
    if (end - cursor > 0) return this.hide();
    const reader = new AutocompleteReader(this.options.trigger);
    const found = reader.check(driver.getLastNChars(this.options.lookBehind ?? 30), cursor, this.options.validBit);
    if (!found) return this.hide();
    this.start = found.absoluteStart;
    const request = ++this.request;
    Promise.resolve(this.options.suggest(found.typed)).then(items => {
      // Uma resposta antiga (de uma busca na API) não pode passar por cima
      // do que já foi digitado depois.
      if (request !== this.request) return;
      this.items = items;
      this.index = 0;
      if (items.length) this.show();else this.hide();
    });
  };
  show() {
    if (!this.container) return;
    this.active = true;
    this.render();
    const driver = editorDriver(this.editor);
    const menu = this.container.firstElementChild;
    if (!driver || !menu) return;

    // Mesma conta do flarum/mentions: abaixo do cursor, ou acima quando não
    // cabe, e sem sair pela direita do editor. As coordenadas do cursor são
    // relativas ao textarea; o contêiner fica no canto do pai dele.
    const coordinates = driver.getCaretCoordinates(this.start);
    const parent = this.container.parentElement;
    // Pelos retângulos, e não por offsetTop: o flarum/mentions pode embrulhar
    // o textarea num div posicionado depois que o contêiner foi criado.
    const textareaRect = this.textarea.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();
    const caretTop = textareaRect.top - parentRect.top + coordinates.top;
    let left = textareaRect.left - parentRect.left + coordinates.left;
    // Acima do cursor quando cabe na tela; senão, embaixo dele.
    const above = caretTop - menu.offsetHeight - 6;
    let top = parentRect.top + above >= 0 ? above : caretTop + 22;
    if (left + menu.offsetWidth > parent.clientWidth) left = Math.max(0, parent.clientWidth - menu.offsetWidth);
    menu.style.top = `${top}px`;
    menu.style.left = `${left}px`;
  }
  hide() {
    this.request++;
    if (!this.active) return;
    this.active = false;
    this.items = [];
    if (this.container) m.render(this.container, null);
  }
  move(delta) {
    const count = this.items.length;

    // As setas param nas pontas, sem dar a volta.
    this.index = Math.max(0, Math.min(count - 1, this.index + delta));
    this.render();
    this.container?.querySelector('.active')?.scrollIntoView({
      block: 'nearest'
    });
  }
  choose(index) {
    const item = this.items[index];
    const driver = editorDriver(this.editor);
    if (!item || !driver) return;

    // `start` é a posição logo depois do gatilho; o -1 o inclui na troca.
    driver.replaceBeforeCursor(this.start - 1, item.replacement + ' ', false);
    this.hide();
  }
  render() {
    if (!this.container) return;
    const header = this.options.header?.();
    m.render(this.container, m("ul", {
      className: classList('Dropdown-menu', 'CanelaAutocomplete', this.options.className),
      role: "listbox"
    }, header && m("li", {
      className: "Dropdown-header"
    }, header), this.items.map((item, i) => m("li", {
      key: item.key,
      className: classList({
        active: i === this.index
      }),
      role: "option",
      "aria-selected": i === this.index ? 'true' : 'false'
    }, m("button", {
      type: "button"
      // mousedown, e não click: o click tiraria o foco do textarea
      // antes, e o cursor iria para o fim do texto.
      ,
      onmousedown: e => {
        e.preventDefault();
        this.choose(i);
      }
    }, item.label)))));
  }
}
flarum.reg.add('ramon-canela', 'forum/features/composer/Autocomplete', Autocomplete);